import { useTranslation } from 'react-i18next';
import { ArrowUp, ExternalLink, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { deriveVoteState } from '@/lib/template-suggestions/deriveVoteState';
import { SuggestionThumbnail } from '@/components/template-ideas/SuggestionThumbnail';
import type { TemplateSuggestion } from '@/lib/template-suggestions/types';

interface SuggestionCardProps {
  suggestion: TemplateSuggestion;
  rank?: number;
  pending: boolean;
  onToggleVote: (suggestion: TemplateSuggestion) => void;
}

export function SuggestionCard({ suggestion, rank, pending, onToggleVote }: SuggestionCardProps) {
  const { t } = useTranslation();
  const voteState = deriveVoteState({
    status: suggestion.status,
    viewerHasVoted: suggestion.viewer_has_voted,
    pending,
  });

  return (
    <Card data-testid="suggestion-card" data-suggestion-id={suggestion.id}>
      <CardContent className="flex gap-4 p-4">
        <div className="flex w-14 shrink-0 flex-col items-center gap-1">
          <Button
            variant={voteState === 'voted' ? 'default' : 'secondary'}
            size="sm"
            className="h-12 w-12 flex-col gap-0"
            disabled={voteState === 'locked'}
            onClick={() => onToggleVote(suggestion)}
            aria-label={voteState === 'voted' ? t('templateIdeas.voted') : t('templateIdeas.vote')}
            data-testid="suggestion-vote"
          >
            {voteState === 'voted' ? <Check className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
            <span className="text-xs font-semibold">{suggestion.vote_count}</span>
          </Button>
          {typeof rank === 'number' && (
            <span className="text-xs text-muted-foreground">#{rank}</span>
          )}
        </div>

        <SuggestionThumbnail url={suggestion.thumbnail_url} className="h-24 w-16" />

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-semibold">{suggestion.title}</h3>
            <Badge variant="outline" className="capitalize">{suggestion.platform}</Badge>
            {suggestion.status === 'promoted' && (
              <Badge variant="secondary">{t('templateIdeas.promotedBadge')}</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{suggestion.use_case}</p>
          {suggestion.note && <p className="text-sm">{suggestion.note}</p>}
          <div className="flex items-center gap-3 pt-1 text-xs text-muted-foreground">
            {suggestion.submitted_by_name && <span>{suggestion.submitted_by_name}</span>}
            <a
              href={suggestion.submitted_url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 hover:text-foreground"
            >
              <ExternalLink className="h-3 w-3" />
              {t('templateIdeas.openPost')}
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
